import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { listPosts, getPublicSiteSettings } from '@/features/blog/services/blogApi';
import type { PaginatedResponse, Post } from '@/shared/types/api';
import type { PublicSiteSettings } from '@/features/blog/services/blogApi';
import { format } from 'date-fns';
import NewsFeed from '@/features/news/components/NewsFeed';
import Sidebar from '@/shared/components/Sidebar';

export default function HomePage() {
  const { data: settings } = useQuery<PublicSiteSettings>({
    queryKey: ['site-settings'],
    queryFn: getPublicSiteSettings,
    staleTime: 60_000,
  });

  const { data } = useQuery<PaginatedResponse<Post>>({
    queryKey: ['posts', 'home'],
    queryFn: () => listPosts({ page: 1, perPage: 3 }),
    enabled: !settings?.blog_maintenance_mode,
  });

  return (
    <div>
      <Helmet>
        <title>Vladyslav Chebotarov — Software Developer</title>
        <meta name="description" content="Personal website and blog of Vladyslav Chebotarov. Senior iOS Developer and Team Lead writing about software development, architecture, and technology." />
        <link rel="canonical" href="https://codder.cc" />
        <meta property="og:title" content="Vladyslav Chebotarov — Software Developer" />
        <meta property="og:description" content="Personal website and blog. Senior iOS Developer and Team Lead." />
        <meta property="og:url" content="https://codder.cc" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Vladyslav Chebotarov" />
        <link rel="alternate" type="application/rss+xml" title="Blog RSS Feed" href="https://codder.cc/api/v1/blog/feed.xml" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Vladyslav Chebotarov',
            url: 'https://codder.cc',
            author: { '@type': 'Person', name: 'Vladyslav Chebotarov' },
          })}
        </script>
      </Helmet>

      <NewsFeed />

      {settings?.blog_maintenance_mode ? (
        <section className="mt-8">
          <MaintenanceShield
            title={settings.blog_maintenance_title}
            text={settings.blog_maintenance_text}
          />
        </section>
      ) : (
        data && data.posts.length > 0 && (
          <section className="mt-8">
            <h2 className="text-base font-bold mb-0 pb-3 text-foreground">Latest Posts</h2>
            <hr />
            {data.posts.map((post) => (
              <div key={post.id} className="py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <Link to={`/blog/${post.id}/${post.slug}`} className="text-accent no-underline hover:underline font-bold">
                  {post.title}
                </Link>
                <span className="text-xs text-muted ml-3">
                  {format(new Date(post.published_at ?? post.created_at), 'yyyy.MM.dd')}
                </span>
              </div>
            ))}
            <div className="mt-3">
              <Link to="/blog" className="text-accent text-sm no-underline hover:underline">
                all posts &rarr;
              </Link>
            </div>
          </section>
        )
      )}

      <div className="mt-12">
        <Sidebar />
      </div>
    </div>
  );
}

function MaintenanceShield({ title, text }: { title: string; text: string }) {
  return (
    <div
      className="py-10 px-6 text-center border border-border rounded"
      style={{ backgroundColor: 'rgba(255,168,106,0.04)' }}
    >
      <div className="text-4xl mb-4">🛡</div>
      <h3 className="text-base font-bold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted">{text}</p>
    </div>
  );
}
